import React, { useContext } from "react";
import styles from "./article.module.scss";
import VisibilitySensor from "react-visibility-sensor";

import { Controller, Scene } from "react-scrollmagic";
import { Tween, Timeline } from "react-gsap";

import radio from "../../../img/clock-radio-dieter-rams.jpg"; // intro
import mac from "../../../img/mac.jpg"; // is innovative
import smeg from "../../../img/smeg-appliances.jpg"; // makes a product useful
import sakred from "../../../img/sakred-glassware-collection-karim-rashid.jpg"; // is aesthetic
import muji from "../../../img/muji-cd-player-naoto-fukusawa.png"; // makes a product understandable
import roundish from "../../../img/roundish-chair-naoto-fukusawa.png"; // is honest
import eames from "../../../img/eames-lounge-chair-charles-and-ray-eames.jpg"; // is long-lasting
import orbit from "../../../img/u-turn-orbit.jpg"; // is thorough down to the last detail
import ikea from "../../../img/ikea-ps-2017-vases-recycled-grass.jpg"; // is environmentally friendly
import stool from "../../../img/prince-aha-stool-philippe-starck.jpg"; // is as little design as possible
import plumen from "../../../img/plumen-samuel-wilkinson.jpg"; // is unobtrusive

import radiof from "../../../img/filter/clock-radio-filter.jpeg";
import macf from "../../../img/filter/mac-filter.jpeg";
import smegf from "../../../img/filter/smeg-filter.jpeg";
import sakredf from "../../../img/filter/sakred-filter.jpeg";
import mujif from "../../../img/filter/muji-filter.png";
import roundishf from "../../../img/filter/chair-filter.png";
import eamesf from "../../../img/filter/eames-filter.jpeg";
import orbitf from "../../../img/filter/orbit-filter.jpeg";
import ikeaf from "../../../img/filter/ikea-filter.jpeg";
import stoolf from "../../../img/filter/stool-filter.jpeg";
import plumenf from "../../../img/filter/plumen-filter.jpeg";

import { ThemeContext } from "../../../context";

const VisibilityContainer = ({ className, children, description, response, name }) => {
  const { setThemeName, setImageData } = useContext(ThemeContext);
  return (
    <VisibilitySensor
      delayedCall={true}
      onChange={isVisible => {
        if (isVisible) {
          setThemeName(className);
          setImageData({ name, description, response });
        }
      }}
    >
      {children}
    </VisibilitySensor>
  );
};

export const Article = ({ getHeight }) => {
  return (
    <>
      <Controller>
        <Scene duration={getHeight} triggerHook="0.2" offset="200">
          <Timeline>
            {masterData.map(data => (
              <Tween from={{ opacity: 1 }} to={{ opacity: 0 }}>
                <section>
                  <VisibilityContainer
                    description={data.text.description}
                    name={data.text.name}
                    className={data.className}
                    response={data.text.response}
                  >
                    <figure>
                      <figcaption>
                        <i>{data.img.title}</i> {data.img.artist}
                        <span className={styles.credit}>
                          <a href={data.img.href}>Photo: {data.img.credit}</a>
                        </span>
                      </figcaption>
                       <span
                        style={{
                          backgroundImage: `url(${data.img.url})`,
                          }}
                      > 
                        <img className={styles.filter}
                          src={data.img.filter}
                          alt={data.img.title}
                        />
                        <p className={styles.clear}></p>
                      </span>

                    </figure>
                  </VisibilityContainer>
                  <p className={styles.principleDescription}>
                    {data.text.description}
                    <span className={styles.response}>
                      {data.text.response}
                    </span>
                  </p>
                </section>
              </Tween>
            ))}
          </Timeline>
        </Scene>
      </Controller>
    </>
  );
};

const masterData = [
  {
    img: {
      title: "Braun Clock Radio",
      artist: "by Dieter Rams, 1978.",
      credit: "HIGHSNOBIETY",
      href: "http://highsnobiety.com",
      url: radio,
      size: "0 0 960 576",
      filter: radiof
    },
    text: {
      name: `Yasmin Pessoa`,
      description: `In the '80s, prolific industrial designer Dieter Rams laid out
    what he believed to be the ten most crucial aspects of good
    design.`,
      response: `These ten principles are outlined below, with my own
    interpretation of each below its respective quote. Hover over images to view their
    descriptions.`
    },
    className: "salmon"
  },
  {
    img: {
      title: "Apple Macintosh 128K",
      artist: "by Apple Inc., 1984",
      credit: "Mac-history",
      href: "http://mac-history.net",
      url: mac,
      size: "0 0 970 646",
      filter: macf
    },
    text: {
      name: `01. Good design is innovative`,
      description: `“The possibilities for innovation are not, by any means,
  exhausted. Technological development is always offering new
  opportunities for innovative design. But innovative design
  always develops in tandem with innovative technology, and can
  never be an end in itself.”`,
      response: ` Design’s ability to innovate is perhaps one of its most
  crucial qualities, because of its potential to advance
  humanity. The original MacIntosh was the first PC built for
  the average person that featured a graphical user interface.
  This was a massive advancement in not only computing, but
  also the experience for each person interacting with a
  MacIntosh.`
    },
    className: "yellow"
  },
  {
    img: {
      title: "Prince Aha Stool",
      artist: "by Philippe Starck, 1999",
      credit: "MoMA",
      href: "http://moma.org",
      url: stool,
      size: "0 0 2000 929",
      filter: stoolf
    },
    text: {
      name: `02. Good design is as little design as possible`,
      description: `“Less, but better – because it concentrates on the essential
      aspects, and the products are not burdened with
      non-essentials. Back to purity, back to simplicity.”`,
      response: `Overthinking doesn’t necessarily lead to a high-quality
      result. These stools by Philippe Starck are minimalistic yet
      gorgeous in their form, color, and texture, while serving a
      functional purpose.`
    },
    className: "sky"
  },
  {
    img: {
      title: "Sakred glassware collection",
      artist: "by Karim Rashid, 2019",
      credit: "Dezeen",
      href: "http://dezeen.com",
      url: sakred,
      size: "0 0 2364 3579",
      filter: sakredf
    },
    text: {
      name: `03. Good design is aesthetic`,
      description: `“The aesthetic quality of a product is integral to its
  usefulness because products we use every day affect our person
  and our well-being. But only well-executed objects can be
  beautiful.”`,
      response: `Products developed entirely with an emphasis on form, and no
  attention given to appearance, do not maximize their
  potential as an object. A well-designed product balances its
  focus between function and visual quality. These glass
  pieces are not only beautiful, but versatile and reversible.`
    },
    className: "darkBlue"
  },
  {
    img: {
      title: "Smeg Appliances",
      artist: "by Smeg Group S.p.A.",
      credit: "Smeg USA",
      href: "http://smegusa.com",
      url: smeg,
      size: "0 0 670 375",
      filter: smegf
    },
    text: {
      name: `04. Good design makes a product useful`,
      description: `“A product is bought to be used. It has to satisfy certain
  criteria, not only functional, but also psychological and
  aesthetic. Good design emphasizes the usefulness of a product
  whilst disregarding anything that could possibly detract from
  it.”`,
      response: `Products that are merely created to be beautiful serve no
  purpose other than being decoration pieces. These retro
  appliances from Smeg are meant to be used daily, while
  making the kitchen more beautiful.`
    },
    className: "aqua"
  },
  {
    img: {
      title: "CD Player",
      artist: "by Naoto Fukasawa, 1999",
      credit: "Muji",
      href: "http://muji.us",
      url: muji,
      size: "0 0 1920 1080",
      filter: mujif
    },
    text: {
      name: `05. Good design makes a product understandable`,
      description: `“It clarifies the product’s structure. Better still, it can
      make the product talk. At best, it is self-explanatory.”`,
      response: `A product needs to be intuitive to be well-designed. If a
      product is meant to be accessible by as many users possible,
      the average person needs to be able to easily understand how
      to use the product. Simplicity can be a powerful asset, as
      seen in the design of this CD player.`
    },
    className: "salmon"
  },
  {
    img: {
      title: "Roundish Armchair",
      artist: "by Naoto Fukusawa, 2018",
      credit: "twentytwentyone",
      href: "http://twentytwentyone",
      url: roundish,
      size: "0 0 812 807",
      filter: roundishf
    },
    text: {
      name: `06. Good design is honest`,
      description: `“It does not make a product more innovative, powerful or
      valuable than it really is. It does not attempt to manipulate
      the consumer with promises that cannot be kept.”`,
      response: `An object does not need unnecessary embellishments to prove
      its value. Its value lies in the thoughtfulness of its
      design. This chair, with its body made from a single piece
      of wood, is both elegant and functional.`
    },
    className: "yellow"
  },
  {
    img: {
      title: "Orbit Plus",
      artist: "by U-Turn Audio",
      credit: "Fat City Turntables",
      url: orbit,
      size: "0 0 1280 720",
      filter: orbitf
    },
    text: {
      name: `07. Good design is thorough down to the last detail`,
      description: `“Nothing must be arbitrary or left to chance. Care and
      accuracy in the design process show respect towards the user.”`,
      response: `Any flaw in a design has the ability to take away from the
      impact of the design as a whole.`
    },
    className: "sky"
  },
  {
    img: {
      title: "IKEA PS 2017",
      artist: "vases made of recycled glass, 2017",
      credit: "Dezeen",
      href: "http://dezeen.com",
      url: ikea,
      size: "0 0 889 592",
      filter: ikeaf
    },
    text: {
      name: `08. Good design is environmentally friendly`,
      description: `“Design makes an important contribution to the preservation of
      the environment. It conserves resources and minimizes physical
      and visual pollution throughout the lifecycle of the product.”`,
      response: `When an industry has the opportunity to damage the planet as
      little as it possibly can, it must. These vases from Ikea
      were blown from 100% recycled glass, and Ikea plans to use
      only 100% renewable and recycled materials by 2030.`
    },
    className: "darkBlue"
  },
  {
    img: {
      title: "Eames Lounge Chair",
      artist: "by Charles and Ray Eames",
      credit: "Design Within Reach",
      href: "http://dwr.com",
      url: eames,
      size: "0 0 873 550",
      filter: eamesf
    },
    text: {
      name: `09. Good design is long-lasting`,
      description: `“It avoids being fashionable and therefore never appears
      antiquated. Unlike fashionable design, it lasts many years –
      even in today’s throwaway society.”`,
      response: `A product that focuses on following trends rather than
      solving problems is often forgettable. The Eames chair is
      perhaps the most well-known piece of modern furniture
      despite being designed in 1956, partly because of its
      timeless and purposeful form.`
    },
    className: "aqua"
  },
  {
    img: {
      title: "Plumen Light Bulb",
      artist: "by Samuel Wilkinson, 2010",
      credit: "Samuel Wilkinson",
      href: "http://samuelwilkinson.com",
      url: plumen,
      size: "0 0 1000 1359",
      filter: plumenf
    },
    text: {
      name: `10. Good design is unobtrusive`,
      description: `“Products fulfilling a purpose are like tools. They are
      neither decorative objects nor works of art. Their design
      should therefore be both neutral and restrained, to leave room
      for the user’s self-expression.”`,
      response: `I believe that good design makes life easier. The Plumen lightbulb is 
      low-energy and aesthetically pleasing, but does not add any unnecessary features 
      that make the product more difficult to use, and that don't contribute to its purpose.`
    },
    className: "salmon"
  }
];
